import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Checkbox,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';

import { AchievementAssetsPanel } from '@components/Asset';
import { BasicButton } from '@components/Button';
import { PageTitle } from '@components/shared';
import entityColor from '@util/entityColor';
import entityIcon from '@util/entityIcon';
import { toErrorMessage } from '@util/toErrorMessage';

const CATEGORY_OPTIONS = [
  { value: '1', label: 'Consistency' },
  { value: '2', label: 'Volume' },
  { value: '3', label: 'Milestone' },
  { value: '4', label: 'Strength' },
];

const CRITERIA_OPTIONS = [
  { value: '1', label: 'Session count' },
  { value: '2', label: 'Streak days' },
  { value: '3', label: 'Session volume (lb)' },
  { value: '4', label: 'Lifetime volume (lb)' },
  { value: '5', label: 'Exercise max count' },
  { value: '6', label: 'Block complete' },
];

export default function AdminAchievementDetail() {
  const { achievementId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('1');
  const [criteriaType, setCriteriaType] = useState('1');
  const [criteriaValue, setCriteriaValue] = useState(0);
  const [allowsMultiple, setAllowsMultiple] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const achievementQuery = useQuery({
    queryKey: ['admin-achievement', achievementId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(`/achievements/${achievementId}/`, {
        signal,
      });
      return data.response?.[0];
    },
    enabled: Boolean(achievementId),
  });

  const achievement = achievementQuery.data;
  const isDeleted = achievement?.status_formatted === 'deleted';
  const isActive = achievement?.status_formatted === 'active';
  const isRetired = achievement?.status_formatted === 'retired';

  useEffect(() => {
    if (!achievement) return;
    setName(achievement.name || '');
    setKey(achievement.key || '');
    setDescription(achievement.description || '');
    setCategory(String(achievement.category || 1));
    setCriteriaType(String(achievement.criteria_type || 1));
    setCriteriaValue(achievement.criteria_value ?? 0);
    setAllowsMultiple(!!achievement.allows_multiple);
    setSortOrder(achievement.sort_order ?? 0);
    setConfirmDelete(false);
  }, [achievement]);

  function reloadAchievement() {
    queryClient.invalidateQueries({
      queryKey: ['admin-achievement', achievementId],
    });
    queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
  }

  function onSave() {
    if (!achievementId || isDeleted) return;
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    axios
      .put(`/achievements/${achievementId}/`, {
        name: name.trim(),
        key: key.trim() || null,
        description: description.trim() || null,
        category: Number(category),
        criteria_type: Number(criteriaType),
        criteria_value: Number(criteriaValue) || 0,
        allows_multiple: allowsMultiple,
        sort_order: Number(sortOrder) || 0,
      })
      .then(() => {
        toast.success('Achievement saved');
        reloadAchievement();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSaving(false));
  }

  function onActivate() {
    setStatusBusy(true);
    axios
      .post(`/achievements/${achievementId}/activate/`, {})
      .then(() => {
        toast.success('Achievement activated');
        reloadAchievement();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatusBusy(false));
  }

  function onRetire() {
    setStatusBusy(true);
    axios
      .post(`/achievements/${achievementId}/retire/`, {})
      .then(() => {
        toast.success('Achievement retired');
        reloadAchievement();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatusBusy(false));
  }

  function onDelete() {
    setStatusBusy(true);
    axios
      .delete(`/achievements/${achievementId}/`)
      .then(() => {
        toast.success('Achievement deleted');
        navigate('/achievements');
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatusBusy(false));
  }

  if (achievementQuery.isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  if (!achievement) {
    return (
      <Alert color="red" title="Not found">
        Achievement could not be loaded.
      </Alert>
    );
  }

  return (
    <div>
      <Group mb="sm">
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => navigate('/achievements')}
          aria-label="Back to achievements"
        >
          <IconArrowLeft size={18} />
        </ActionIcon>
      </Group>

      <PageTitle
        title={name || achievement.name}
        subtitle="Criteria, repeatability, and trophy art"
        icon={entityIcon.achievement()}
        iconColor={entityColor.achievement}
      />

      <Stack gap="md" mt="md">
        <Group gap="xs">
          <Badge
            size="sm"
            variant="light"
            color={
              isActive ? 'teal' : isRetired ? 'gray' : isDeleted ? 'red' : 'blue'
            }
          >
            {achievement.status_formatted}
          </Badge>
        </Group>

        {isDeleted ? (
          <Alert variant="light" color="red" title="Deleted">
            This achievement is soft-deleted and cannot be edited.
          </Alert>
        ) : (
          <Paper p="md" withBorder radius="sm">
            <Stack gap="md">
              <TextInput
                label="Name"
                value={name}
                onChange={(event) => setName(event.currentTarget.value)}
                required
                disabled={saving || statusBusy}
              />
              <TextInput
                label="Key"
                value={key}
                onChange={(event) => setKey(event.currentTarget.value)}
                disabled={saving || statusBusy}
              />
              <Textarea
                label="Description"
                value={description}
                onChange={(event) => setDescription(event.currentTarget.value)}
                minRows={2}
                autosize
                disabled={saving || statusBusy}
              />
              <Select
                label="Category"
                data={CATEGORY_OPTIONS}
                value={category}
                onChange={(value) => setCategory(value || '1')}
                disabled={saving || statusBusy}
              />
              <Select
                label="Award when"
                data={CRITERIA_OPTIONS}
                value={criteriaType}
                onChange={(value) => setCriteriaType(value || '1')}
                disabled={saving || statusBusy}
              />
              <NumberInput
                label="Criteria value"
                value={criteriaValue}
                onChange={(value) => setCriteriaValue(value ?? 0)}
                min={0}
                disabled={saving || statusBusy}
              />
              <NumberInput
                label="Sort order"
                value={sortOrder}
                onChange={(value) => setSortOrder(value ?? 0)}
                disabled={saving || statusBusy}
              />
              <Checkbox
                label="Allow multiple awards"
                checked={allowsMultiple}
                onChange={(event) =>
                  setAllowsMultiple(event.currentTarget.checked)
                }
                disabled={saving || statusBusy}
              />
              <Group>
                <BasicButton
                  text="Save"
                  onClick={onSave}
                  loading={saving}
                  disabled={saving || statusBusy}
                  color="var(--brand-brick)"
                  variant="filled"
                  style={{
                    background: 'var(--brand-brick)',
                    borderColor: 'var(--brand-brick)',
                  }}
                  primary
                />
                {!isActive && (
                  <BasicButton
                    text="Activate"
                    onClick={onActivate}
                    loading={statusBusy}
                    disabled={statusBusy || saving}
                    color="teal"
                    variant="light"
                  />
                )}
                {!isRetired && (
                  <BasicButton
                    text="Retire"
                    onClick={onRetire}
                    loading={statusBusy}
                    disabled={statusBusy || saving}
                    color="gray"
                    variant="outline"
                  />
                )}
              </Group>
              <Group mt="xs">
                {confirmDelete ? (
                  <>
                    <Text size="sm" fw={700}>
                      Delete this achievement?
                    </Text>
                    <BasicButton
                      text="Confirm delete"
                      onClick={onDelete}
                      loading={statusBusy}
                      color="red"
                      variant="filled"
                    />
                    <BasicButton
                      text="Cancel"
                      onClick={() => setConfirmDelete(false)}
                      disabled={statusBusy}
                      color="gray"
                      variant="outline"
                    />
                  </>
                ) : (
                  <BasicButton
                    text="Delete"
                    onClick={() => setConfirmDelete(true)}
                    disabled={statusBusy || saving}
                    color="red"
                    variant="outline"
                  />
                )}
              </Group>
            </Stack>
          </Paper>
        )}

        <AchievementAssetsPanel
          achievementId={achievementId}
          assets={achievement.assets || []}
          readOnly={isDeleted}
          onChanged={reloadAchievement}
        />
      </Stack>
    </div>
  );
}
